// src/context/DatabaseContext.js

import React, { createContext, useContext, useState, useCallback } from "react";
import axios from "axios";
import { AUTH_TOKEN, REACT_APP_API, ORG_ID, DOMAIN_ID } from "../../constants";

const DatabaseContext = createContext();

export const DatabaseProvider = ({ children }) => {
  // -----------------------------------------------------------
  // State: store data you want to share across your app
  // -----------------------------------------------------------
  const [dbNames, setDbNames] = useState([]); // from getUniqueDatabasenames
  const [dbList, setDbList] = useState([]); // from listDatabase
  const [totalDBCount, setTotalDBCount] = useState(0); // from listDatabase
  const [dbEndpoints, setDbEndpoints] = useState([]); // from listDatabaseEndpoints
  const [connections, setConnections] = useState([]); // from listConnections
  const [usernames, setUsernames] = useState([]); // from getUniqueUsernames

  // This can store all tables for each DB (if you want to hold them in memory).
  // E.g. { "test_db": ["table1", "table2"], "sales_db": ["tableX", ...], ...}
  const [tablesMap, setTablesMap] = useState({});

  // -----------------------------------------------------------
  // 1. Fetch Unique Database Names
  // -----------------------------------------------------------
  const fetchDbNames = useCallback(async () => {
    try {
      const response = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/getUniqueDatabasenames`,
        {
          orgId: ORG_ID(),
          tenantId: DOMAIN_ID(),
        },
        {
          headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true },
        }
      );
      setDbNames(response.data?.db_names || []);
    } catch (error) {
      console.error("Error fetching database names:", error?.response?.status, error?.message);
      // No built-in 'showNotification' in the context, but you can handle it externally.
    }
  }, []);

  // -----------------------------------------------------------
  // 2. Fetch the full Database List (with pagination & filters)
  // -----------------------------------------------------------
  const fetchDbList = useCallback(async (filters = [], limit = 10, page_id = 1) => {
    try {
      const response = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/listDatabase`,
        {
          orgId: ORG_ID(),
          tenantId: DOMAIN_ID(),
          filters,
          limit,
          page_id,
        },
        {
          headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true },
        }
      );
      const data = response.data?.data || [];
      setDbList(data);
      setTotalDBCount(response.data?.total_count || 0);

      // If you want to handle total_count or total_pages, you can return them:
      return {
        data,
        totalCount: response.data?.total_count || 0,
        totalPages: response.data?.total_pages || 1,
      };
    } catch (error) {
      console.error("Error fetching database list:", error?.response?.status, error?.message);
      return { data: [], totalCount: 0, totalPages: 1 };
    }
  }, []);

  // -----------------------------------------------------------
  // 3. Fetch Endpoints / VMs
  // -----------------------------------------------------------
  const fetchDbEndpoints = useCallback(async (filters = [], limit = 10, page_id = 1) => {
    try {
      const response = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/listDatabaseEndpoints`,
        {
          orgId: ORG_ID(),
          tenantId: DOMAIN_ID(),
          filters,
          limit,
          pageId: page_id,
        },
        {
          headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true },
        }
      );
      const data = response.data?.data || [];
      setDbEndpoints(data);
      return {
        data,
        totalCount: response.data?.total_count || 0,
        totalPages: response.data?.total_pages || 1,
      };
    } catch (error) {
      console.error("Error fetching database endpoints:", error?.response?.status, error?.message);
      return { data: [], totalCount: 0, totalPages: 1 };
    }
  }, []);

  // -----------------------------------------------------------
  // 4. Fetch Connections
  // -----------------------------------------------------------
  const fetchConnections = useCallback(async (filters = [], limit = 8, page_id = 1) => {
    try {
      const payload = {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        filters,
        limit,
        page_id,
        requestId: "",
      };
      const response = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/listConnections`,
        payload,
        {
          headers: {
            "X-Authorization": AUTH_TOKEN(),
            userid: localStorage.getItem("userId"),
            email: localStorage.getItem("UserName"),
            withCredentials: true,
          },
        }
      );
      const data = response.data?.data || [];
      setConnections(data);
      return {
        data,
        totalCount: response.data?.total_count || 0,
        totalPages: response.data?.total_pages || 1,
      };
    } catch (error) {
      console.error("Error fetching connections:", error?.response?.status, error?.message);
      return { data: [], totalCount: 0, totalPages: 1 };
    }
  }, []);

  // -----------------------------------------------------------
  // 5. Fetch Unique Usernames
  // -----------------------------------------------------------
  const fetchUsernames = useCallback(async () => {
    try {
      const response = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/getUniqueUsernames`,
        {
          orgId: ORG_ID(),
          tenantId: DOMAIN_ID(),
        },
        {
          headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true },
        }
      );
      setUsernames(response.data?.usernames || []);
    } catch (error) {
      console.error("Error fetching usernames:", error?.response?.status, error?.message);
    }
  }, []);

  // -----------------------------------------------------------
  // 6. Fetch Tables for a specific Database
  // -----------------------------------------------------------
  const fetchTablesForDb = useCallback(async (dbName,host) => {
    // if (!dbName) return [];
    try {
      const response = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/listTables`,
        {
          orgId: ORG_ID(),
          tenantId: DOMAIN_ID(),
          dbName:dbName || "",
          host: host || "",
        },
        {
          headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true },
        }
      );
      const tableData = response.data?.data || {};
      setTablesMap((prev) => ({
        ...prev,
        [dbName]: Object.entries(tableData).map(([tableName, columns]) => ({
          name: tableName,
          columns: columns
        }))
      }));
      return Object.keys(tableData);
    } catch (error) {
      console.error(`Error fetching tables for DB ${dbName}:`, error?.response?.status, error?.message);
      return [];
    }
  }, []);

  // -----------------------------------------------------------
  // Provide data + fetch methods to all consumers
  // -----------------------------------------------------------
  return (
    <DatabaseContext.Provider
      value={{
        // Data
        dbNames,
        dbList,
        totalDBCount,
        dbEndpoints,
        connections,
        usernames,
        tablesMap,

        // Setters (in case child wants to manipulate directly)
        setDbNames,
        setDbList,
        setDbEndpoints,
        setConnections,
        setUsernames,
        setTablesMap,

        // Methods
        fetchDbNames,
        fetchDbList,
        fetchDbEndpoints,
        fetchConnections,
        fetchUsernames,
        fetchTablesForDb,
      }}
    >
      {children}
    </DatabaseContext.Provider>
  );
};

export const useDatabase = () => {
  return useContext(DatabaseContext);
};
